import React, { useEffect, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import DataTable, { TableColumn } from "react-data-table-component";
import { useSelector } from 'react-redux';
import { RootState } from "../../../store/store";
import { getClaimedLoanTasks, getUnclaimedLoanTasks } from "../../../services/loanService";
import {
    BpmTaskWithApplication,
    BpmTaskWorkItemType,
    GetClaimedLoanTasksStage,
    GetUnclaimedLoanTasksStage,
    LoanApplicationStatus
} from "../../../services/schemas/loanServiceSchemas";
import { toast } from "react-toastify";
import { Card, Badge, Button, Form, Row, Col } from 'react-bootstrap';
import { formatCurrency } from "../../../utils/formatters";
import './WorkItems.css';
import './WorkItemTracker.css';

// Custom styles similar to WorkItems.tsx
const customStyles = {
    rows: {
        style: {
            minHeight: '45px',
        }
    },
    headCells: {
        style: {
            fontWeight: 600,
            paddingLeft: '15px',
            paddingRight: '8px',
            fontSize: '14px',
            justifyContent: 'flex-start'
        }
    },
    cells: {
        style: {
            paddingLeft: '15px',
            paddingRight: '8px',
        }
    }
};

interface WorkItemTrackerProps {}

const WorkItemTracker: React.FC<WorkItemTrackerProps> = () => {
    const [clientFullScreen, setClientFullScreen] = useState(false);
    const handleFullScreen = () => setClientFullScreen(!clientFullScreen);
    const navigate = useNavigate();

    const authState = useSelector((state: RootState) => state.auth);
    const userEmail = useSelector((state: RootState) => state.auth.user?.email);
    const userGroups = useSelector((state: RootState) => state.auth.user?.userGroups) || [];

    // State for all work items
    const [allTasks, setAllTasks] = useState<BpmTaskWithApplication[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Search state
    const [searchCustomerName, setSearchCustomerName] = useState('');
    const [searchCustomerNameInput, setSearchCustomerNameInput] = useState('');
    const [searchStage, setSearchStage] = useState<GetClaimedLoanTasksStage | ''>('');
    const [filterAssignment, setFilterAssignment] = useState<'all' | 'assigned' | 'unassigned'>('all');


    // Debounce effect for search customer name
    useEffect(() => {
        const timer = setTimeout(() => {
            // Only search if input is empty or has at least 3 characters
            if (searchCustomerNameInput === '' || searchCustomerNameInput.length >= 3) {
                setSearchCustomerName(searchCustomerNameInput);
            }
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [searchCustomerNameInput]);

    // Function to fetch all tasks (both claimed and unclaimed)
    const fetchAllTasks = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Fetch both claimed and unclaimed tasks in parallel
            const [claimedTasksResponse, unclaimedTasksResponse] = await Promise.all([
                getClaimedLoanTasks({
                    customerName: searchCustomerName || undefined,
                    stage: searchStage || undefined,
                    page: 0,
                    size: 1000
                }),
                getUnclaimedLoanTasks({
                    customerName: searchCustomerName || undefined,
                    stage: searchStage as GetUnclaimedLoanTasksStage || undefined,
                    page: 0,
                    size: 1000
                })
            ]);

            const claimedTasks = claimedTasksResponse.content || [];
            console.log('Claimed Tasks:', claimedTasks);
            const unclaimedTasks = unclaimedTasksResponse.content || [];
            console.log('Unclaimed Tasks:', unclaimedTasks);


            // Combine both claimed and unclaimed tasks
            const allTasksData = [...claimedTasks, ...unclaimedTasks];
            console.log('Combined Tasks:', allTasksData);
            // Remove duplicates based on task id (in case a task appears in both lists)
            const uniqueTasks = Array.from(
                new Map(allTasksData.map(task => [task.task.taskId, task])).values()
            );

            setAllTasks(uniqueTasks);
        } catch (error) {
            console.error('Error in fetchAllTasks:', error);
            setError(error instanceof Error ? error : new Error('Failed to fetch work items'));
            toast.error('Failed to fetch work items');
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch tasks when search parameters change
    useEffect(() => {
        fetchAllTasks();
    }, [searchCustomerName, searchStage]);


    const handleViewWorkItem = (taskId: string) => {
        navigate(`/work-items/${taskId}`);
    };

    const handleProcessWorkItem = (taskId: string, workItemType: BpmTaskWorkItemType) => {
        if (workItemType === BpmTaskWorkItemType.PROCESS_LEAD) {
            navigate(`/loan-application/new?taskId=${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.VERIFY_DOCUMENTS) {
            navigate(`/verification/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.UPLOAD_DOCUMENTS) {
            navigate(`/loan-application-documents/${encodeURIComponent(taskId)}?fromTask=true`);
        } else if (workItemType === BpmTaskWorkItemType.CREDIT_ASSESSMENT) {
            navigate(`/credit-assessment/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.UPLOAD_AGREEMENT) {
            navigate(`/loan-agreement/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.VERIFY_COLLATERAL) {
            navigate(`/collateral-verification/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.APPROVE_LOAN) {
            navigate(`/credit-approval/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.DISBURSE_FUNDS) {
            navigate(`/disbursements/${encodeURIComponent(taskId)}`);
        } else {
            navigate(`/work-items/${taskId}/process`);
        }
    };

    // Format the work item type for display
    const formatWorkItemType = (type: BpmTaskWorkItemType) => {
        return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    };

    // Get badge color based on work item type - using distinct colors
    const getWorkItemTypeBadgeClass = (type: BpmTaskWorkItemType) => {
        switch (type) {
            case BpmTaskWorkItemType.PROCESS_LEAD:
                return 'dark';
            case BpmTaskWorkItemType.CAPTURE_APPLICATION:
                return 'primary';
            case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
                return 'info';
            case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
                return 'warning';
            case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
                return 'danger';
            case BpmTaskWorkItemType.VERIFY_COLLATERAL:
                return 'secondary';
            case BpmTaskWorkItemType.REVIEW_APPLICATION:
                return 'success';
            case BpmTaskWorkItemType.APPROVE_LOAN:
                return 'primary';
            case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
                return 'info';
            case BpmTaskWorkItemType.DISBURSE_FUNDS:
                return 'success';
            default:
                return 'secondary';
        }
    };

    // Get custom badge style for better color distinction
    const getWorkItemTypeBadgeStyle = (type: BpmTaskWorkItemType): React.CSSProperties => {
        switch (type) {
            case BpmTaskWorkItemType.PROCESS_LEAD:
                return { backgroundColor: '#212529', color: 'white' }; // Dark
            case BpmTaskWorkItemType.CAPTURE_APPLICATION:
                return { backgroundColor: '#0d6efd', color: 'white' }; // Blue
            case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
                return { backgroundColor: '#0dcaf0', color: 'black' }; // Cyan
            case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
                return { backgroundColor: '#ffc107', color: 'black' }; // Yellow
            case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
                return { backgroundColor: '#dc3545', color: 'white' }; // Red
            case BpmTaskWorkItemType.VERIFY_COLLATERAL:
                return { backgroundColor: '#6f42c1', color: 'white' }; // Purple
            case BpmTaskWorkItemType.REVIEW_APPLICATION:
                return { backgroundColor: '#20c997', color: 'white' }; // Teal
            case BpmTaskWorkItemType.APPROVE_LOAN:
                return { backgroundColor: '#198754', color: 'white' }; // Green
            case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
                return { backgroundColor: '#fd7e14', color: 'white' }; // Orange
            case BpmTaskWorkItemType.DISBURSE_FUNDS:
                return { backgroundColor: '#28a745', color: 'white' }; // Success Green
            default:
                return { backgroundColor: '#6c757d', color: 'white' }; // Gray
        }
    };

    // Get icon for work item types
    const getWorkItemTypeIcon = (type: BpmTaskWorkItemType) => {
        switch (type) {
            case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
                return 'bi-file-check';
            case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
                return 'bi-cloud-upload';
            case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
                return 'bi-cash-stack';
            case BpmTaskWorkItemType.CAPTURE_APPLICATION:
                return 'bi-file-earmark-text';
            case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
                return 'bi-file-earmark-contract';
            case BpmTaskWorkItemType.REVIEW_APPLICATION:
                return 'bi-clipboard-check';
            case BpmTaskWorkItemType.APPROVE_LOAN:
                return 'bi-check-circle';
            case BpmTaskWorkItemType.PROCESS_LEAD:
                return 'bi-person-plus';
            case BpmTaskWorkItemType.VERIFY_COLLATERAL:
                return 'bi-shield-check';
            case BpmTaskWorkItemType.DISBURSE_FUNDS:
                return 'bi-cash';
            default:
        return 'bi-tasks';
        }
    };

    // Get application status badge
    const getApplicationStatusBadge = (status?: LoanApplicationStatus) => {
        if (!status) return <Badge bg="secondary">Unknown</Badge>;

        switch (status) {
            case LoanApplicationStatus.DRAFT:
                return <Badge bg="secondary">Draft</Badge>;
            case LoanApplicationStatus.LEAD_PROCESSING:
                return <Badge bg="info">Lead Processing</Badge>;
            case LoanApplicationStatus.PENDING_SUBMISSION:
                return <Badge bg="warning">Pending Submission</Badge>;
            case LoanApplicationStatus.SUBMITTED:
                return <Badge bg="primary">Submitted</Badge>;
            case LoanApplicationStatus.DOCUMENT_COLLECTION:
                return <Badge bg="info">Document Collection</Badge>;
            case LoanApplicationStatus.VERIFICATION_IN_PROGRESS:
                return <Badge bg="info">Verification In Progress</Badge>;
            case LoanApplicationStatus.VERIFICATION_REJECTED:
                return <Badge bg="danger">Verification Rejected</Badge>;
            case LoanApplicationStatus.VERIFICATION_COMPLETE:
                return <Badge bg="success">Verification Complete</Badge>;
            case LoanApplicationStatus.EVALUATION_IN_PROGRESS:
                return <Badge bg="info">Evaluation In Progress</Badge>;
            case LoanApplicationStatus.REJECTED_EVALUATION:
                return <Badge bg="danger">Rejected</Badge>;
            case LoanApplicationStatus.AGREEMENT_PREPARATION:
                return <Badge bg="info">Agreement Preparation</Badge>;
            case LoanApplicationStatus.AGREEMENT_READY:
                return <Badge bg="success">Agreement Ready</Badge>;
            case LoanApplicationStatus.COLLATERAL_VERIFICATION:
                return <Badge bg="info">Collateral Verification</Badge>;
            case LoanApplicationStatus.REJECTED_COLLATERAL:
                return <Badge bg="danger">Collateral Rejected</Badge>;
            case LoanApplicationStatus.PENDING_DISBURSEMENT:
                return <Badge bg="warning">Pending Disbursement</Badge>;
            case LoanApplicationStatus.DISBURSEMENT_IN_PROGRESS:
                return <Badge bg="info">Disbursement In Progress</Badge>;
            case LoanApplicationStatus.ACTIVE:
                return <Badge bg="success">Active</Badge>;
            case LoanApplicationStatus.COMPLETED:
                return <Badge bg="success">Completed</Badge>;
            case LoanApplicationStatus.REJECTED:
                return <Badge bg="danger">Rejected</Badge>;
            case LoanApplicationStatus.CANCELLED:
                return <Badge bg="dark">Cancelled</Badge>;
            default:
                return <Badge bg="secondary">{String(status).replace(/_/g, ' ')}</Badge>;
        }
    };

    // Filter tasks based on assignment filter
    const filteredTasks = allTasks.filter(taskWithApp => {
        if (filterAssignment === 'assigned') {
            return taskWithApp.task.assignee;
        } else if (filterAssignment === 'unassigned') {
            return !taskWithApp.task.assignee;
        }
        return true; // 'all'
    });

    const getCustomerName = (row: BpmTaskWithApplication): string => {
        return row.task.company?.companyName ||
            row.application?.company?.companyName ||
            row.application?.individual?.fullNames ||
            row.task.fullName ||
            'Unknown Customer';
    };

    // Table columns
    const columns: TableColumn<BpmTaskWithApplication>[] = [
        {
            name: "Customer / Application",
            cell: (row) => {
                const customerName = getCustomerName(row);
                return (
                <div className="d-flex flex-column">
                        <div className="fw-bold text-primary">
                            {customerName}
                        </div>
                        {row.application?.applicationNumber && (
                            <small className="text-muted">App #: {row.application.applicationNumber}</small>
                        )}
                        {row.application?.loanType && (
                            <small className="text-muted">
                                <i className="bi bi-tag me-1"></i>
                                {String(row.application.loanType).replace(/_/g, ' ')}
                            </small>
                    )}
                </div>
                );
            },
            sortable: true,
            sortFunction: (a, b) => getCustomerName(a).localeCompare(getCustomerName(b)),
            minWidth: '220px',
            style: { paddingLeft: '15px' }
        },
        {
            name: "Stage",
            cell: (row) => (
                <div className="d-flex flex-column align-items-start">
                    <span
                        className="badge"
                        style={getWorkItemTypeBadgeStyle(row.task.workItemType)}
                    >
                        <i className={`bi ${getWorkItemTypeIcon(row.task.workItemType)} me-1`}></i>
                        {formatWorkItemType(row.task.workItemType)}
                    </span>
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => a.task.workItemType.localeCompare(b.task.workItemType),
            minWidth: '180px'
        },
        {
            name: "Loan Amount",
            cell: (row) => (
                <div className="d-flex flex-column">
                    {row.application?.loanDetails?.loanAmount ? (
                        <div className="fw-medium text-success">
                            {formatCurrency(row.application.loanDetails.loanAmount)}
                        </div>
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => (a.application?.loanDetails?.loanAmount || 0) - (b.application?.loanDetails?.loanAmount || 0),
            minWidth: '130px'
        },
        {
            name: "Assigned To",
            cell: (row) => (
                <div className="d-flex flex-column">
                    {row.task.assignee ? (
                        <span
                            className="badge"
                            style={{ backgroundColor: '#198754', color: 'white' }}
                        >
                            <i className="bi bi-person-check-fill me-1"></i>
                            {row.task.assignee}
                        </span>
                    ) : (
                        <span
                            className="badge"
                            style={{ backgroundColor: '#ffc107', color: 'black' }}
                        >
                            <i className="bi bi-person-x-fill me-1"></i>
                            Unassigned
                        </span>
                    )}
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => {
                const aAssigned = a.task.assignee ? 1 : 0;
                const bAssigned = b.task.assignee ? 1 : 0;
                return bAssigned - aAssigned;
            },
            minWidth: '140px'
        },
        {
            name: "Created / Due",
            cell: (row) => (
                <div className="d-flex flex-column">
                    <div className="fw-medium">
                        <i className="bi bi-calendar-plus me-1"></i>
                        {new Date(row.task.createdDate).toLocaleDateString()}
                    </div>
                    {row.task.dueDate && (
                        <small className={`${new Date(row.task.dueDate) < new Date() ? 'text-danger' : 'text-muted'}`}>
                            <i className="bi bi-calendar-event me-1"></i>
                            Due: {new Date(row.task.dueDate).toLocaleDateString()}
                        </small>
                    )}
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => new Date(a.task.createdDate).getTime() - new Date(b.task.createdDate).getTime(),
            minWidth: '150px'
        },
        {
            name: 'Actions',
            cell: (row: BpmTaskWithApplication) => (
                <div className="d-flex justify-content-center w-100">
                    <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => handleViewWorkItem(row.task.taskId)}
                        title="View work item details"
                        className="p-1"
                        style={{ fontSize: '12px' }}
                    >
                        <i className="bi bi-eye me-1"></i>
                        View
                    </Button>
                </div>
            ),
            minWidth: '80px',
            center: true
        },
    ];

    return (
        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3 work-item-tracker">
            <div className="container-fluid">
                {/* Breadcrumb */}
                <div className="row mb-3">
                    <div className="col">
                        <div className="page-breadcrumb d-flex align-items-center">
                            <div className="pe-3">
                                <div className="px-2 rounded-circle bg-primary-light">
                                    <i className="bi bi-kanban text-primary"></i>
                                </div>
                            </div>
                            <div className="flex-fill">
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/work-items">Tasks</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">Application Tracker</li>
                                </ol>
                            </div>
                            <div className="ms-auto">
                                <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    onClick={fetchAllTasks}
                                    disabled={isLoading}
                                >
                                    <i className={`bi ${isLoading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'} me-1`}></i>
                                    {isLoading ? 'Loading...' : 'Refresh'}
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleFullScreen}
                                >
                                    {clientFullScreen ? (
                                        <><i className="bi bi-fullscreen-exit me-1"></i> Exit Fullscreen</>
                                    ) : (
                                        <><i className="bi bi-fullscreen me-1"></i> Fullscreen</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search filters */}
                <div className="row mb-3">
                    <div className="col-12">
                        <Card>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold mb-1">
                                                <i className="bi bi-person me-1"></i>
                                                Customer Name
                                                {searchCustomerNameInput.length > 0 && searchCustomerNameInput.length < 3 && (
                                                    <small className="text-muted ms-2">(min 3 characters)</small>
                                                )}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by customer name (min 3 characters)..."
                                                value={searchCustomerNameInput}
                                                onChange={(e) => setSearchCustomerNameInput(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold mb-1">
                                                <i className="bi bi-funnel me-1"></i>
                                                Stage
                                            </Form.Label>
                                            <Form.Select
                                                value={searchStage}
                                                onChange={(e) => setSearchStage(e.target.value as GetClaimedLoanTasksStage | '')}
                                            >
                                                <option value="">All Stages</option>
                                                <option value={GetClaimedLoanTasksStage.PROCESS_LEAD}>Process Lead</option>
                                                <option value={GetClaimedLoanTasksStage.CAPTURE_APPLICATION}>Capture Application</option>
                                                <option value={GetClaimedLoanTasksStage.UPLOAD_DOCUMENTS}>Upload Documents</option>
                                                <option value={GetClaimedLoanTasksStage.VERIFY_DOCUMENTS}>Verify Documents</option>
                                                <option value={GetClaimedLoanTasksStage.CREDIT_ASSESSMENT}>Credit Assessment</option>
                                                <option value={GetClaimedLoanTasksStage.VERIFY_COLLATERAL}>Verify Collateral</option>
                                                <option value={GetClaimedLoanTasksStage.REVIEW_APPLICATION}>Review Application</option>
                                                <option value={GetClaimedLoanTasksStage.APPROVE_LOAN}>Approve Loan</option>
                                                <option value={GetClaimedLoanTasksStage.UPLOAD_AGREEMENT}>Upload Agreement</option>
                                                <option value={GetClaimedLoanTasksStage.DISBURSE_FUNDS}>Disburse Funds</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold mb-1">
                                                <i className="bi bi-person-check me-1"></i>
                                                Assignment
                                            </Form.Label>
                                            <Form.Select
                                                value={filterAssignment}
                                                onChange={(e) => setFilterAssignment(e.target.value as 'all' | 'assigned' | 'unassigned')}
                                            >
                                                <option value="all">All Tasks</option>
                                                <option value="assigned">Assigned Only</option>
                                                <option value="unassigned">Unassigned Only</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-secondary"
                                            className="w-100"
                                            onClick={() => {
                                                setSearchCustomerName('');
                                                setSearchCustomerNameInput('');
                                                setSearchStage('');
                                                setFilterAssignment('all');
                                            }}
                                        >
                                            <i className="bi bi-x-circle me-1"></i>
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Statistics */}
                <div className="row mb-3">
                    <div className="col-md-3 col-6 mb-2">
                        <Card className="text-center h-100 border-0" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <Card.Body className="py-3">
                                <div className="d-flex align-items-center justify-content-center mb-2">
                                    <div className="rounded-circle p-3" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                                        <i className="fa fa-folder fs-3 text-primary"></i>
                                    </div>
                                </div>
                                <h4 className="text-primary mb-1">{filteredTasks.length}</h4>
                                <small className="text-muted">Total Applications</small>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3 col-6 mb-2">
                        <Card className="text-center h-100 border-0" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <Card.Body className="py-3">
                                <div className="d-flex align-items-center justify-content-center mb-2">
                                    <div className="rounded-circle p-3" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)' }}>
                                        <i className="fa fa-check-circle fs-3 text-success"></i>
                                    </div>
                                </div>
                                <h4 className="text-success mb-1">{filteredTasks.filter(t => t.task.assignee).length}</h4>
                                <small className="text-muted">Assigned</small>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3 col-6 mb-2">
                        <Card className="text-center h-100 border-0" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <Card.Body className="py-3">
                                <div className="d-flex align-items-center justify-content-center mb-2">
                                    <div className="rounded-circle p-3" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                                        <i className="fa fa-user-times fs-3 text-warning"></i>
                                    </div>
                                </div>
                                <h4 className="text-warning mb-1">{filteredTasks.filter(t => !t.task.assignee).length}</h4>
                                <small className="text-muted">Unassigned</small>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3 col-6 mb-2">
                        <Card className="text-center h-100 border-0" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <Card.Body className="py-3">
                                <div className="d-flex align-items-center justify-content-center mb-2">
                                    <div className="rounded-circle p-3" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
                                        <i className="fa fa-exclamation-triangle fs-3 text-danger"></i>
                                    </div>
                                </div>
                                <h4 className="text-danger mb-1">
                                    {filteredTasks.filter(t => t.task.dueDate && new Date(t.task.dueDate) < new Date()).length}
                                </h4>
                                <small className="text-muted">Overdue</small>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Work items table */}
                <div className="row">
                    <div className="col-12">
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <div className="d-flex align-items-center">
                                    <h6 className="card-title mb-0" style={{ marginRight: '8px' }}>
                                        <i className="bi bi-kanban me-2"></i>
                                        Application Progress Tracker
                                        <Badge bg="primary" className="ms-2">{filteredTasks.length}</Badge>
                                    </h6>
                                    <div className="text-muted small ms-auto" >
                                        <i className="bi bi-info-circle me-1"></i>
                                        Track all loan applications and their current processing stage
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {error ? (
                                    <div className="alert alert-danger">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        Error loading applications: {error instanceof Error ? error.message : 'Unknown error'}
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <DataTable
                                            columns={columns}
                                            data={filteredTasks}
                                            pagination
                                            paginationPerPage={25}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            highlightOnHover
                                            striped
                                            responsive
                                            progressPending={isLoading}
                                            customStyles={customStyles}
                                            persistTableHead
                                            fixedHeader
                                            fixedHeaderScrollHeight="500px"
                                            defaultSortFieldId={1}
                                            progressComponent={
                                                <div className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-2 text-muted">Loading applications...</p>
                                                </div>
                                            }
                                            noDataComponent={
                                                <div className="p-5 text-center">
                                                    <i className="bi bi-inbox fs-1 text-muted"></i>
                                                    <p className="mt-3 text-muted">No applications found matching your criteria</p>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSearchCustomerName('');
                                                            setSearchCustomerNameInput('');
                                                            setSearchStage('');
                                                            setFilterAssignment('all');
                                                        }}
                                                    >
                                                        Clear Filters
                                                    </Button>
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkItemTracker;
